import type { FileCluster } from '../view-models/file-clusters.ts';

/**
 * Renders the cluster patterns owned by a derivedContext or business
 * function under its settings row. Read-only — cluster editing
 * happens through the LLM onboarding orchestrator, not the SPA.
 *
 * The id passed in is the *prefixed* public id (`comp_*` or
 * `bizfn_*`); cluster classification stores raw slugs, so we strip
 * the prefix before matching.
 */
export function ClusterPatterns({
  clusters,
  ownerKind,
  ownerId,
}: {
  clusters: FileCluster[] | undefined;
  ownerKind: 'derived_context' | 'business_function';
  ownerId: string;
}) {
  if (!clusters) return null;
  const rawSlug = stripPrefix(ownerId, ownerKind);
  const matched = clusters.filter((c) => {
    const ids =
      ownerKind === 'derived_context' ? c.derived_context_ids : c.business_function_ids;
    return ids.includes(rawSlug);
  });
  if (matched.length === 0) {
    return (
      <div
        className="muted"
        style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}
      >
        No cluster patterns attributed to this {ownerKind.replace('_', ' ')}.
      </div>
    );
  }
  // Sort by priority asc, then pattern asc for stable display.
  const sorted = [...matched].sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    return a.pattern.localeCompare(b.pattern);
  });
  return (
    <div style={{ marginTop: '0.5rem' }}>
      <div
        className="muted"
        style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}
      >
        {matched.length} cluster pattern{matched.length === 1 ? '' : 's'}:
      </div>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem',
        }}
      >
        {sorted.map((c) => (
          <li
            key={c.id}
            style={{
              fontSize: '0.85rem',
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              display: 'flex',
              alignItems: 'baseline',
              gap: '0.5rem',
            }}
          >
            <code
              style={{
                background: 'var(--color-surface-2, rgba(127,127,127,0.08))',
                padding: '0.1rem 0.4rem',
                borderRadius: '3px',
                wordBreak: 'break-all',
              }}
            >
              {c.pattern}
            </code>
            {c.skill_ids.length > 0 ? (
              <span
                className="muted"
                style={{ fontSize: '0.75rem', fontFamily: 'inherit' }}
              >
                → {c.skill_ids.join(', ')}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function stripPrefix(
  id: string,
  kind: 'derived_context' | 'business_function',
): string {
  const prefix = kind === 'derived_context' ? 'comp_' : 'bizfn_';
  return id.startsWith(prefix) ? id.slice(prefix.length) : id;
}
